import { Checkbox, CheckboxCard } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Textarea } from '@1trading/react/components/ui/textarea';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SOURCES_OF_FUNDS, SourceOfFunds } from '@/constants/institution';
import { InstitutionFieldDocument } from '@/features/institution/components/fields/document';
import type { InstitutionExpectedActivityOrDefaults } from '@/features/institution/expected-activity/schema';
import { DocumentSource } from '@/services/api/documents';

const InstitutionExpectedActivityFieldsetSourceOfFunds = () => {
  const form = useFormContext<InstitutionExpectedActivityOrDefaults>();
  const { t } = useTranslation();
  const sourceOfFunds = form.watch('sourceOfFunds');

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="sourceOfFunds"
        render={() => (
          <FormItem>
            <FormLabel>{t('institutionDetails.expectedActivity.sourceOfFunds')}*</FormLabel>
            <FormDescription>{t('institutionDetails.expectedActivity.sourceOfFundsDescription')}</FormDescription>
            <FormControl aria-label={t('institutionDetails.expectedActivity.sourceOfFunds')}>
              {/* biome-ignore lint/a11y/useSemanticElements: Makeshift checkboxgroup since it's not a native role */}
              <div className="space-y-4" role="group">
                {SOURCES_OF_FUNDS.map((option) => (
                  <FormField
                    key={option}
                    control={form.control}
                    name="sourceOfFunds"
                    render={({ field }) => (
                      <FormItem>
                        <CheckboxCard variant="outline">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(option)}
                              variant="cards"
                              onCheckedChange={(checked) => field.onChange(checked ? [...(field.value ?? []), option] : field.value?.filter((value) => value !== option))}
                            />
                          </FormControl>
                          <FormLabel contained>{t(`institutionDetails.expectedActivity.sourceOfFundsOptions.${option}`)}</FormLabel>
                        </CheckboxCard>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {sourceOfFunds?.includes(SourceOfFunds.OTHER) ? (
        <FormField
          control={form.control}
          name="sourceOfFundsOther"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.expectedActivity.sourceOfFundsOther')}*</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder={t('institutionDetails.expectedActivity.placeholders.sourceOfFundsOther')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      <InstitutionFieldDocument<InstitutionExpectedActivityOrDefaults>
        description={t('institutionDetails.expectedActivity.sourceOfFundsDocumentDescription')}
        label={t('institutionDetails.expectedActivity.sourceOfFundsDocument')}
        name="sourceOfFundsDocument"
        source={DocumentSource.PROOF_OF_FUNDS}
      />
    </div>
  );
};
InstitutionExpectedActivityFieldsetSourceOfFunds.displayName = 'InstitutionExpectedActivityFieldsetSourceOfFunds';

export { InstitutionExpectedActivityFieldsetSourceOfFunds };
